/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { FormErrors, UseSubmitInputDataPayload } from 'hooks';
import React from 'react';
import {
  Submissions,
  InputData,
  ConfirmTableProps,
  ConfirmPropertyHeading,
  ConfirmTable,
  SubmissionErrorMessages,
  SubmissionButton,
  GlobalErrorMessages,
} from 'shared';
import { FormContent } from '../../components';
import { CurrentFormPage } from '../../store';
import { ConfirmPropertyValue } from './ConfirmPropertyValue';
import { getMappedProperties } from './utils';

const GlobalErrorContent = styled.div`
  outline: none;

  label: GlobalError;
`;

export type ConfirmContentProps = {
  formPage: NonNullable<CurrentFormPage>;
  inputData: InputData;
  onSubmissionClick: (payload: UseSubmitInputDataPayload) => Promise<void>;
  submissionErrors: FormErrors['submission'];
  globalErrors: FormErrors['global'];
  buttonDisabled: {
    next: boolean;
    previous: boolean;
  };
};
export const ConfirmContent = React.forwardRef<
  HTMLDivElement,
  ConfirmContentProps
>(
  (
    {
      formPage,
      inputData,
      onSubmissionClick,
      submissionErrors,
      globalErrors,
      buttonDisabled,
    },
    ref,
  ) => {
    const { design, required, submissions } = formPage;
    const mappedProperties = getMappedProperties(formPage, inputData);
    const handleButtonClick = (endpoint: SubmissionButton['endpoint']) => {
      onSubmissionClick({ endpoint });
    };
    const {
      confirmPagePropertyTitleCell,
      confirmPagePropertyTitleFont,
      confirmPageInputCell,
      confirmPageInputFont,
      confirmPageTableBorder,
    } = design.colors;

    const contents: ConfirmTableProps['contents'] = mappedProperties.map(
      ({ property, inputItem }) => {
        const { propertyId, name, displayRequiredLabel } = property;
        const isRequired = (required && required.includes(propertyId)) || false;
        return {
          HeadingElement: (
            <ConfirmPropertyHeading
              required={isRequired}
              name={name}
              displayRequiredLabel={displayRequiredLabel}
              color={confirmPagePropertyTitleFont}
            />
          ),
          ContentElement: inputItem ? (
            <ConfirmPropertyValue
              property={property}
              key={propertyId}
              inputItem={inputItem}
              color={confirmPageInputFont}
            />
          ) : (
            <div />
          ),
        };
      },
    );
    const displaySubmissionError = submissionErrors.length > 0;
    const displayGlobalError = globalErrors.length > 0;
    return (
      <form>
        <FormContent>
          <GlobalErrorContent
            css={{
              marginBottom: displayGlobalError ? '24px' : 0,
              label: 'GlobalErrorContent',
            }}
            ref={ref}
            tabIndex={-1}
          >
            <GlobalErrorMessages
              displayError={displayGlobalError}
              errorMessages={globalErrors}
            />
          </GlobalErrorContent>

          <ConfirmTable
            contents={contents}
            headingBackgroundColor={confirmPagePropertyTitleCell}
            contentBackgroundColor={confirmPageInputCell}
            borderColor={confirmPageTableBorder}
            css={{ marginBottom: '24px', label: 'ConfirmTable' }}
          />

          {submissions && (
            <Submissions
              submissions={submissions}
              onPreviousButtonClick={handleButtonClick}
              onNextButtonClick={handleButtonClick}
              disabled={buttonDisabled}
              design={design}
            />
          )}
          <SubmissionErrorMessages
            displayError={displaySubmissionError}
            errorMessages={submissionErrors}
            css={{
              marginTop: displaySubmissionError ? '8px' : 0,
              label: 'SubmissionError',
            }}
          />
        </FormContent>
      </form>
    );
  },
);
